import { memo } from "react";
import type { QuoteListRow } from "../quoteList/useQuoteListState";
import { AccordionDetails, Checkbox } from "~/common/mui";
import { QuoteCardHeader } from "./QuoteCardHeader";
import {
  OrderCardAccordion,
  OrderCardAccordionSummary,
  StickyCardContainer,
} from "../common/components/v2/OrderAndQuoteSharedStyles";
import Spinner from "~/common/components/Spinner/Spinner";
import type { QuoteListSelectionState } from "../common/components/v2/QuoteTable/useQuoteListSelectionState";
import { useTranslation } from "react-i18next";
import { QuoteLineList } from "./QuoteLineList";
import { canQuoteListRowBeSelected } from "../quoteList/utils";
import { AccordionSummaryToggle } from "../common/components/v2/AccordionSummaryToggle";
import { isErrorEntry } from "../common/utils/util";

interface Props {
  row: QuoteListRow;
  toggleOpen: (row: QuoteListRow) => void;
  toggleSelectQuote: (row: QuoteListRow) => void;
  toggleSelectLineItem: (soNumber: string, quoteLineNo: string) => void;
  selectionPerDocument: QuoteListSelectionState["selectionPerDocument"];
}

export const QuoteCard = memo(
  ({ row, toggleOpen, toggleSelectQuote, toggleSelectLineItem, selectionPerDocument }: Props) => {
    const { t } = useTranslation();
    const { open, loadingDetails } = row;

    const renderDetails = (row: QuoteListRow) => {
      if (!row.details) {
        return null;
      }
      const { quoteLines, soNumber } = row.details;
      const selectionStatus = selectionPerDocument[soNumber]!;
      return (
        <QuoteLineList
          quoteLines={quoteLines}
          showCheckboxes={true}
          {...selectionStatus}
          toggleLine={(lineNo) => toggleSelectLineItem(soNumber, lineNo)}
        />
      );
    };

    const renderHeaderCheckbox = (row: QuoteListRow) => {
      const isSelected = selectionPerDocument[row.data.soNumber]?.isAllSelected ?? false;
      const canBeSelected = canQuoteListRowBeSelected(row);
      const disabled = row.loadingDetails || !canBeSelected;

      return (
        <Checkbox
          checked={isSelected}
          onChange={() => {
            if (!isErrorEntry(row)) {
              toggleSelectQuote(row);
            }
          }}
          disabled={disabled}
          data-test-id={"quoteLine-check-all"}
        />
      );
    };

    return (
      <StickyCardContainer open={open}>
        <QuoteCardHeader row={row} sticky={open} renderHeaderCheckbox={renderHeaderCheckbox} />
        <OrderCardAccordion
          expanded={open}
          onChange={() => {
            if (!isErrorEntry(row)) {
              toggleOpen(row);
            }
          }}
        >
          <OrderCardAccordionSummary expandIcon={null}>
            <AccordionSummaryToggle
              open={open}
              showLabel={t("quoteList.orderCard.accordionSummary.show")}
              hideLabel={t("quoteList.orderCard.accordionSummary.hide")}
              disabled={isErrorEntry(row)}
            />
          </OrderCardAccordionSummary>
          <AccordionDetails sx={{ pb: 0 }}>
            {open && loadingDetails && <Spinner />}
            {open && !loadingDetails && renderDetails(row)}
          </AccordionDetails>
        </OrderCardAccordion>
      </StickyCardContainer>
    );
  },
);
